/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import {
  type ContextValue,
  Provider,
  Tab as TabCore,
  type TabProps as TabCoreProps,
  useContextProps,
} from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface TabProps extends Omit<TabCoreProps, 'style' | 'className'> {
  /** Whether the tab is bordered. (Default: false) */
  isBordered?: boolean;
  /** Tab size. (Default: 32) */
  size?: keyof typeof lineSizeVariants;
  style?: stylex.StyleXStyles;
}

const TabContext = createContext<ContextValue<TabProps, HTMLDivElement>>({});

const Tab = forwardRef((props: TabProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TabContext);

  const { style, size = 32, isBordered = false, ...coreProps } = props;
  let { children } = props;

  return (
    <TabCore
      ref={ref}
      {...stylex.props(
        styles.base,
        isBordered ? styles.bordered : styles.lined,
        isBordered ? borderedSizeVariants[size] : lineSizeVariants[size],
        style
      )}
      {...coreProps}>
      {(renderProps) => {
        children = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(children).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [TextContext, {
              size: TEXT_SIZES[size],
              color: (
                (!isBordered && renderProps.isSelected && 'primary') ||
                (isBordered && renderProps.isSelected && 'interactive') ||
                'secondary'
              ),
            }],
            [IconContext, {
              size: ICON_SIZES[size],
              style: iconAccessoryStyles[size],
              color: (
                (renderProps.isDisabled && 'disabled') ||
                (!isBordered && renderProps.isSelected && 'primary') ||
                (isBordered && renderProps.isSelected && 'interactive') ||
                'secondary'
              ),
            }],
          ]}>
            {typeof children === 'string' || isTextOnly ? <Text>{children}</Text> : children}
          </Provider>
        );
      }}
    </TabCore>
  );
});

const TEXT_SIZES: Record<keyof typeof lineSizeVariants, TextProps['size']> = {
  32: 14,
  48: 16,
};

const ICON_SIZES: Record<keyof typeof lineSizeVariants, number> = {
  32: 16,
  48: 24,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'inline-flex',
    gap: 0,
    justifyContent: 'center',
    position: 'relative',
    textDecoration: 'none',
    textWrap: 'nowrap',
    userSelect: 'none',
  },
  bordered: {
    borderColor: {
      default: $.border12,
      ':is([data-selected])': $.borderInteractiveSilent,
    },
    borderRadius: 999,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  lined: {
    borderBottomColor: {
      default: 'transparent',
      ':is([data-selected])': $.borderContrast,
    },
    borderBottomStyle: 'solid',
    borderBottomWidth: 2,
    marginBottom: -1,
  }
});

const lineSizeVariants = stylex.create({
  32: {
    height: 32,
    paddingBottom: 17,
    paddingTop: 15,
  },
  48: {
    height: 48,
    paddingBottom: 17,
    paddingTop: 11,
  },
});

const borderedSizeVariants = stylex.create({
  32: {
    paddingBlock: 7,
    paddingInline: 12,
  },
  48: {
    paddingBlock: 10,
    paddingInline: 16,
  },
});

const iconAccessoryStyles = stylex.create({
  32: { marginRight: 6 },
  48: { marginRight: 8 },
});

export { Tab, TabContext, type TabProps };
